<div class="flex flex-col gap-4">
  <!-- Card head: what the grid is on the left, how to read its fill on the
       right. The ramp is the only legend that belongs here — it explains the
       colour scale itself, which you need before reading a single cell. The
       two special states are exceptions to that scale and stay in the footer. -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex flex-col gap-0.5">
      <h4 class="text-sm font-semibold text-gray-900">Klikmomenten per uur</h4>

      <p class="text-xs text-gray-500">Elk vakje is één uur van deze dag.</p>
    </div>

    <div class="flex items-center gap-1.5 text-xs text-gray-500">
      <span>Minder</span>
      @for (step of rampSteps; track step) {
        <span
          class="scm-heatmap__swatch"
          [attr.data-state]="'clicks'"
          [attr.data-level]="step"
        ></span>
      }
      <span>Meer</span>
    </div>
  </div>

  @if (!rows().length) {
    <p class="text-sm text-gray-500 py-8 text-center">
      Op deze dag waren er geen signaleringsblokken actief en is er niet geklikt.
    </p>
  } @else {
    <div class="overflow-x-auto scrollbar-width-thin">
      <table class="scm-heatmap">
        <caption class="sr-only">
          Aantal kliks per signaleringsblok per uur op
          {{
            dayLabel()
          }}.
        </caption>

        <thead>
          <tr>
            <th scope="col" class="scm-heatmap__row-header"></th>
            @for (hour of hours; track hour) {
              <th scope="col" class="scm-heatmap__hour-header">
                <!-- Only every other label is legible at rest: 24 columns
                     cannot carry 24 labels. The rest are rendered all the same
                     and revealed when their column is hovered, so the
                     crosshair can always name the hour it points at — and so
                     every column keeps the same width either way. -->
                <span
                  class="scm-heatmap__hour-label"
                  [attr.data-hidden]="hour % 2 === 1 ? '' : null"
                  aria-hidden="true"
                >
                  {{ formatHour(hour) }}
                </span>

                <span class="sr-only">{{ formatHour(hour) }}:00</span>
              </th>
            }
            <th scope="col" class="scm-heatmap__total-header">Totaal</th>
          </tr>
        </thead>

        <tbody>
          @for (row of rows(); track row.block.scmId) {
            <tr>
              <th scope="row" class="scm-heatmap__row-header">
                <div class="flex items-center gap-2">
                  @if (row.avatarUrl) {
                    <img
                      [src]="row.avatarUrl"
                      alt=""
                      class="size-8 rounded-md object-cover bg-gray-100 shrink-0"
                    />
                  } @else {
                    <div class="size-8 rounded-md bg-gray-100 shrink-0"></div>
                  }

                  <div class="flex items-center gap-1 min-w-0">
                    <span class="text-sm font-medium text-gray-700 truncate">
                      {{ row.block.label }}
                    </span>
                    @if (row.block.deleted) {
                      <span
                        class="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0"
                      >
                        verwijderd
                      </span>
                    }
                  </div>
                </div>
              </th>

              @for (cell of row.cells; track cell.hour) {
                <!-- The sticky row header names the block on screen, so the
                     tooltip does not repeat it. The accessible name does: in
                     browse mode the cell is read without its header.

                     MatTooltip rather than a native `title`, whose delay is the
                     browser's and cannot be turned off. Its timing and its
                     pointer behaviour come from `TOOLTIP_OPTIONS`, provided by
                     this component — see the comment there. -->
                <td
                  class="scm-heatmap__cell"
                  [attr.data-state]="cell.state"
                  [attr.data-level]="cell.level"
                  [matTooltip]="cell.label"
                  [attr.aria-label]="row.block.label + ' — ' + cell.label"
                  tabindex="0"
                >
                  <!-- No cell prints its value: the grid stays a grid, and the
                       count lives in the tooltip and the sr-only text. -->
                  @if (cell.state === 'clicks' || cell.state === 'zero') {
                    <span class="sr-only">{{ cell.clicks }}</span>
                  } @else {
                    <span class="sr-only">niet van toepassing</span>
                  }
                </td>
              }

              <td class="scm-heatmap__total">{{ row.block.totalClicks }}</td>
            </tr>
          }
        </tbody>

        <!-- The day's total, closing the column the per-block totals stack up
             in. The hour columns are spanned rather than filled: nothing is
             being said about any single hour here. -->
        <tfoot>
          <tr>
            <th scope="row" class="scm-heatmap__row-header scm-heatmap__foot-label">
              Totaal
            </th>

            <td [attr.colspan]="hours.length"></td>

            <td class="scm-heatmap__total scm-heatmap__foot-total">
              {{ totalClicks() }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="flex flex-wrap items-center gap-4 text-xs text-gray-500">
      <div class="flex items-center gap-1.5">
        <span class="scm-heatmap__swatch" [attr.data-state]="'zero'"></span>
        <span>Actief, geen kliks</span>
      </div>

      <div class="flex items-center gap-1.5">
        <span class="scm-heatmap__swatch" [attr.data-state]="'unknown'"></span>
        <span>Niet aantoonbaar actief</span>
      </div>

      <!-- Only shown on today: on any earlier day no cell can be pending, and a
           legend entry for a state that is not on the grid is noise. -->
      @if (pendingFromHour() !== null) {
        <div class="flex items-center gap-1.5">
          <span class="scm-heatmap__swatch" [attr.data-state]="'pending'"></span>
          <span>Nog niet voorbij</span>
        </div>
      }
    </div>
  }
</div>
